import { API_BASE, authHeaders } from './apiAuth'

/**
 * Pinning, through the API Worker.
 *
 * Kept in the database rather than in the browser, because every other fact
 * about a person here is — and a preference that lives only in one browser is
 * the one that goes missing the day they sit at another machine.
 */

export type PinKind = 'project' | 'skill'

export interface Pin {
  kind: PinKind
  ref: string
  /** When it was pinned. The latest pin sits first (Huanrui, 2026-10-02). */
  pinned_at?: string
}

/** A set of `kind:ref` keys, which is the shape every caller actually wants. */
export type PinSet = Set<string>

export const pinKey = (kind: PinKind, ref: string): string => `${kind}:${ref}`

export async function listPins(): Promise<PinSet> {
  const response = await fetch(`${API_BASE}/api/pins`, {
    // No surface named: the website sends its sign-in and the Miro panel its
    // panel token. It was pinned to 'web', which is why the panel had no pins.
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(`Could not read your pins (HTTP ${response.status})`)
  const rows = (await response.json()) as Pin[]
  return new Set(rows.map((row) => pinKey(row.kind, row.ref)))
}

/** The same pins, each with when it was pinned, keyed by `kind:ref`. */
export async function listPinTimes(): Promise<Map<string, string>> {
  const response = await fetch(`${API_BASE}/api/pins`, {
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(`Could not read your pins (HTTP ${response.status})`)
  const rows = (await response.json()) as Pin[]
  return new Map(rows.map((row) => [pinKey(row.kind, row.ref), row.pinned_at ?? '']))
}

export async function setPin(kind: PinKind, ref: string, pinned: boolean): Promise<void> {
  const response = await fetch(`${API_BASE}/api/pins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ kind, ref, pinned }),
  })
  if (!response.ok) throw new Error(`Could not save that (HTTP ${response.status})`)
}
